import { useMemo, useRef, useState } from "react";
import type { FinanceCashRecord } from "./finance-workbook-query";
import type { FinanceExportContext } from "./finance-record-export";
import { downloadFinanceRecords } from "./finance-record-export";
import { emptyFinanceFilters, filterFinanceRecords, financeRecordTotals } from "./finance-records";
import type { FinanceRecordFilters } from "./finance-records";

export function useFinanceRecordsWorkspace(records: FinanceCashRecord[], context: FinanceExportContext, initialSearch = "") {
  const [filters, setFilters] = useState<FinanceRecordFilters>(() => ({ ...emptyFinanceFilters(), search: initialSearch }));
  const [page, setPage] = useState(1), [selected, setSelected] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false), [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null), pending = useRef(false);
  const filtered = useMemo(() => filterFinanceRecords(records, filters, context.date), [records, filters, context.date]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / 40));
  const currentPage = Math.min(page, totalPages);
  const change = <K extends keyof FinanceRecordFilters>(key: K, value: FinanceRecordFilters[K]) => {
    setFilters(previous => ({ ...previous, [key]: value })); setPage(1); setSelected(null);
  };
  const exportRecords = async () => {
    if (pending.current) return;
    pending.current = true; setExporting(true); setError(null); setNotice(null);
    try { await downloadFinanceRecords(filtered, filters, context); setNotice(`${filtered.length} kayıt için Excel dosyası hazırlandı.`); }
    catch { setError("Excel dosyası hazırlanamadı. Kayıtlarınız korunuyor; yeniden deneyin."); }
    finally { pending.current = false; setExporting(false); }
  };
  return { filters, change, selected, setSelected, currencies: [...new Set(records.map(record => record.row.currency))].sort(),
    filtered, rows: filtered.slice((currentPage - 1) * 40, currentPage * 40), totals: financeRecordTotals(filtered),
    page: currentPage, setPage, totalPages, exporting, error, notice, exportRecords,
    invalidDates: Boolean(filters.from && filters.to && filters.from > filters.to),
    clear: () => { setFilters(emptyFinanceFilters()); setPage(1); setSelected(null); },
  };
}
